import { TrendingUp } from "lucide-react";
import FooterLiveCounter from "../components/marketing/FooterLiveCounter";

/* V160.2 — Restructured footer.
   New layout (left-to-right on desktop, stacked on mobile):
   1. TopRankPulse intro + tagline
   2. 🤖 AI Agenti (6 live agents + Marketplace)
   3. ✨ Nástroje · Tools (SEO, AI Sim, Cookie audit, Brand Monitor, etc.)
   4. 🔌 Pluginy (WordPress, WooCommerce, Shopify)
   5. 📚 Resources (Blog, Pricing, FAQ)
   6. ⚖️ Legal (Privacy, Terms, GDPR, Cookies, Impressum)
*/
export function LandingFooter({ t }) {
  return (
    <footer className="py-16 px-6 bg-[var(--color-bg)] border-t border-[var(--color-border)]" data-testid="landing-footer">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 mb-12">
          {/* === 1) Brand intro === */}
          <div className="col-span-2 md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 bg-[#a855f7] flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-black font-['Cabinet_Grotesk']">TopRankPulse</span>
            </div>
            <p className="text-[var(--color-text-muted)] text-sm leading-relaxed">
              {t.ft_desc || "AI SEO & Visibility Platform. Track how AI search engines see your website."}
            </p>
            <FooterLiveCounter />
          </div>

          {/* === 2) 🤖 AI Agenti === */}
          <div data-testid="footer-col-agents">
            <h4 className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-4">
              🤖 {t.ft_agents_col || "AI Agenti"}
            </h4>
            <div className="space-y-2">
              <FootLink href="/agent-os/demo/helpdesk" testid="footer-agent-helpdesk">
                {t.ft_agent_help || "Helpdesk Agent"}
                <span className="text-[9px] text-emerald-400 ml-1">€99/mo</span>
              </FootLink>
              <FootLink href="/agent-os/demo/cart-recovery" testid="footer-agent-cart">
                {t.ft_agent_cart || "Cart Recovery"}
                <span className="text-[9px] text-emerald-400 ml-1">€99/mo</span>
              </FootLink>
              <FootLink href="/agent-os/demo/review-reply" testid="footer-agent-review">
                {t.ft_agent_review || "Review Reply"}
                <span className="text-[9px] text-emerald-400 ml-1">€79/mo</span>
              </FootLink>
              <FootLink href="/agent-os/demo/product-desc" testid="footer-agent-pd">
                {t.ft_agent_pd || "Product Desc"}
                <span className="text-[9px] text-emerald-400 ml-1">€79/mo</span>
              </FootLink>
              <FootLink href="/agent-os/demo/newsletter" testid="footer-agent-nl">
                {t.ft_agent_nl || "Newsletter Agent"}
                <span className="text-[9px] text-emerald-400 ml-1">€79/mo</span>
              </FootLink>
              <FootLink href="/agent-os/demo/gdpr-watch" testid="footer-agent-gdpr">
                {t.ft_agent_gdpr || "GDPR Watch"}
                <span className="text-[9px] text-emerald-400 ml-1">€49/mo</span>
              </FootLink>
              <FootLink href="/agent-os/eshop" testid="footer-agent-marketplace">
                <span className="text-[#c084fc] font-bold">{t.ft_agent_market || "→ Marketplace"}</span>
                <span className="text-[9px] text-amber-400 ml-1">14d trial</span>
              </FootLink>
            </div>
          </div>

          {/* === 3) ✨ Nástroje · Tools === */}
          <div data-testid="footer-col-tools">
            <h4 className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-4">
              ✨ {t.ft_tools_col || "Nástroje"}
            </h4>
            <div className="space-y-2">
              <FootLink href="/">
                {t.ft_scan || "AI SEO Scan"}
                <span className="text-[9px] text-emerald-400 ml-1">FREE</span>
              </FootLink>
              <FootLink href="/free-ads-preview">
                {t.ft_adspv || "Ads Preview"}
                <span className="text-[9px] text-emerald-400 ml-1">FREE</span>
              </FootLink>
              <FootLink href="/ai-simulator">{t.ft_sim || "AI Simulator"}</FootLink>
              <FootLink href="/brand-monitor">{t.ft_brand || "Brand Monitor"}</FootLink>
              <FootLink href="/competitor-spy">{t.ft_cs || "Competitor Spy"}</FootLink>
              <FootLink href="/attribution">{t.ft_attr || "AI Attribution"}</FootLink>
              <FootLink href="/seo-autopilot">{t.ft_autopilot || "SEO Autopilot"}</FootLink>
              <FootLink href="/blog-agent">{t.ft_blog_agent || "Blog Agent"}</FootLink>
              <FootLink href="/fix-my-seo">{t.ft_fix || "Fix My SEO"}</FootLink>
              <FootLink href="/revenue-leak">{t.ft_revenue_leak || "Revenue Leak Audit"}</FootLink>
              <FootLink href="/cookie-audit">{t.ft_cookie_audit || "Cookie Audit"}</FootLink>
              <FootLink href="/ads-creator">
                {t.ft_studio || "PulseStudio"}
                <span className="text-[9px] text-[#c084fc] ml-1">NEW</span>
              </FootLink>
            </div>
          </div>

          {/* === 4) 🔌 Pluginy & Integrations === */}
          <div data-testid="footer-col-plugins">
            <h4 className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-4">
              🔌 {t.ft_plugins_col || "Pluginy"}
            </h4>
            <div className="space-y-2">
              <FootLink href="/plugin" testid="footer-plugin-wp">
                {t.ft_wp || "WordPress"}
                <span className="text-[9px] text-emerald-400 ml-1">FREE</span>
              </FootLink>
              <FootLink href="/plugin#woocommerce" testid="footer-plugin-woo">
                {t.ft_woo || "WooCommerce"}
                <span className="text-[9px] text-emerald-400 ml-1">FREE</span>
              </FootLink>
              <FootLink href="/plugin#shopify" testid="footer-plugin-shopify">
                {t.ft_shopify || "Shopify"}
                <span className="text-[9px] text-emerald-400 ml-1">FREE</span>
              </FootLink>
              <FootLink href="/legal-center">{t.ft_legal_gen || "Legal Generator"}</FootLink>
              <FootLink href="/impressum-generator">{t.ft_impressum_gen || "Impressum Gen"}</FootLink>
              <FootLink href="/blog-packs">{t.ft_blog_packs || "Blog Packs"}</FootLink>
            </div>
          </div>

          {/* === 5) 📚 Resources === */}
          <div data-testid="footer-col-resources">
            <h4 className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-4">
              📚 {t.ft_resources || "Resources"}
            </h4>
            <div className="space-y-2">
              <FootLink href="/blog">{t.ft_blog || "Blog"}</FootLink>
              <FootLink href="/#pricing">{t.ft_pricing || "Pricing"}</FootLink>
              <FootLink href="/#faq">{t.ft_faq || "FAQ"}</FootLink>
              <FootLink href="/products/seo-audit">{t.ft_seo_audit || "SEO Audit"}</FootLink>
              <FootLink href="/products/ai-visibility">{t.ft_ai_vis || "AI Visibility"}</FootLink>
              <FootLink href="/agents">{t.ft_agents_landing || "Agents Landing"}</FootLink>
              <FootLink href="/login">{t.ft_login || "Sign In"}</FootLink>
            </div>
          </div>

          {/* === 6) ⚖️ Legal === */}
          <div data-testid="footer-col-legal">
            <h4 className="text-[var(--color-text-muted)] text-xs font-['JetBrains_Mono'] uppercase tracking-wider mb-4">
              ⚖️ {t.ft_legal || "Legal"}
            </h4>
            <div className="space-y-2">
              <FootLink href="/privacy" testid="footer-privacy">{t.ft_privacy || "Privacy Policy"}</FootLink>
              <FootLink href="/terms" testid="footer-terms">{t.ft_terms || "Terms of Service"}</FootLink>
              <FootLink href="/gdpr" testid="footer-gdpr">{t.ft_gdpr || "GDPR"}</FootLink>
              <FootLink href="/cookies">{t.ft_cookies || "Cookie Policy"}</FootLink>
              <FootLink href="/impressum-generator">{t.ft_impressum || "Impressum"}</FootLink>
            </div>
          </div>
        </div>

        {/* === Bottom strip === */}
        <div className="border-t border-[var(--color-border)] pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-zinc-700 text-xs font-['JetBrains_Mono']">
            © 2026 TopRankPulse. {t.ft_rights || "All rights reserved."}
          </p>
          <div className="flex items-center gap-4 text-zinc-700 text-xs">
            <a href="/privacy" className="hover:text-[var(--color-text-muted)]">Privacy</a>
            <span>·</span>
            <a href="/terms" className="hover:text-[var(--color-text-muted)]">Terms</a>
            <span>·</span>
            <a href="/gdpr" className="hover:text-[var(--color-text-muted)]">GDPR</a>
          </div>
        </div>
        <script dangerouslySetInnerHTML={{
          __html: `
            (function(){
              var b=document.getElementById('emergent-badge');
              if(b){document.querySelector('[data-badge-slot]')?.appendChild(b);}
            })();
          `,
        }} />
        <div data-badge-slot className="opacity-10 mt-4" />
      </div>
    </footer>
  );
}

/* Reusable footer link with consistent styling */
function FootLink({ href, children, testid }) {
  return (
    <a
      href={href}
      className="block text-[var(--color-text-muted)] text-sm hover:text-[var(--color-text)] transition-colors"
      data-testid={testid}
    >
      {children}
    </a>
  );
}
